import { useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_GREETING = {
  id: "greeting",
  role: "assistant",
  content:
    "Hey, I'm the on-call print consultant for 3D PRINT HUB Bengaluru. Ask me anything about materials, tolerances, overhangs, or DFM. I'll keep it short and practical.",
};

const genId = () =>
  `msg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** Parse a buffer of raw SSE text into events + leftover remainder. */
const parseSSE = (buffer) => {
  const events = buffer.split("\n\n");
  const remainder = events.pop() || "";
  const parsed = events.map(parseSingleEvent);
  return { parsed, remainder };
};

const parseSingleEvent = (evt) => {
  let event = "message";
  let data = "";
  for (const ln of evt.split("\n")) {
    if (ln.startsWith("event:")) {
      event = ln.slice(6).trim();
    } else if (ln.startsWith("data:")) {
      // Per SSE spec: strip ONLY the single optional space after the colon,
      // preserve everything else so mid-word chunk boundaries keep their spaces.
      let d = ln.slice(5);
      if (d.startsWith(" ")) d = d.slice(1);
      data += d;
    }
  }
  return { event, data };
};

/**
 * useAIChat — encapsulates chat state + SSE streaming.
 * Returns messages, streaming flag, and a send() action.
 */
export default function useAIChat() {
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);

  const appendUserThenAssistant = (text) => {
    const assistantId = genId();
    setMessages((m) => [
      ...m,
      { id: genId(), role: "user", content: text },
      { id: assistantId, role: "assistant", content: "" },
    ]);
    return assistantId;
  };

  const replaceLast = (patch) =>
    setMessages((m) => {
      if (m.length === 0) return m;
      const copy = [...m];
      copy[copy.length - 1] = { ...copy[copy.length - 1], ...patch };
      return copy;
    });

  const appendToLast = (chunk) =>
    setMessages((m) => {
      if (m.length === 0) return m;
      const copy = [...m];
      const prev = copy[copy.length - 1];
      copy[copy.length - 1] = { ...prev, content: (prev.content || "") + chunk };
      return copy;
    });

  const applyEvent = ({ event, data }) => {
    if (event === "session") sessionRef.current = data;
    else if (event === "error") {
      replaceLast({
        content:
          "Sorry — the assistant hit an error. Please try again or WhatsApp +91 80881 63846.",
      });
    } else if (event !== "done") {
      appendToLast(data.replace(/\\n/g, "\n"));
    }
  };

  const readStream = async (res) => {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { parsed, remainder } = parseSSE(buffer);
      buffer = remainder;
      parsed.forEach(applyEvent);
    }
    // Flush any trailing partial event (defensive — backend always sends a final \n\n)
    if (buffer.trim()) {
      const { parsed } = parseSSE(buffer + "\n\n");
      parsed.forEach(applyEvent);
    }
  };

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    appendUserThenAssistant(text);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("network");
      await readStream(res);
    } catch (err) {
      console.warn("[useAIChat] stream failed:", err);
      replaceLast({ content: "Connection issue. Please try again in a moment." });
    } finally {
      setStreaming(false);
    }
  };

  return { messages, streaming, send };
}
